/** 샵관리 — 입고관리 (입고 1건 = FIFO lot). 수량·단가는 매입단위로 입력하면 사용단위로 환산 저장 */
import React from 'react';
import { hiCol } from 'components/grid/hiGrid';
import ShopGridPage from './ShopGridPage';
import { loadTable, persistTable, createUid, num, today, productOptions, fmt } from 'shop/shopStore';
import { kwField, selectField, dateRangeFields, inRange } from 'shop/shopPageHelpers';

const decorate = (tc) => {
  const products = new Map(loadTable(tc, 'product').map((p) => [p.productUid, p]));
  return (l) => ({ ...l, mod: '', useUnit: products.get(l.productUid)?.useUnit || '' });
};

const filter = (rows, v) =>
  rows.filter(
    (r) => r.mod !== 'D'
      && (!v.productUid || r.productUid === v.productUid)
      && inRange(r.inDate, v.fromDate, v.toDate)
      && (!v.keyword || String(r.vendorNm || '').includes(v.keyword))
      && (v.remainOnly !== 'Y' || num(r.remainQty) > 0)
  );

const cfg = {
  gridId: 'sh-stock-in',
  title: '입고관리',
  uidKey: 'lotUid',
  searchDefaults: { productUid: '', keyword: '', fromDate: '', toDate: '', remainOnly: '' },
  searchFields: ({ tc }) => [[
    ...dateRangeFields(),
    selectField('productUid', '제품', productOptions(tc), '200px'),
    kwField('거래처'),
    selectField('remainOnly', '잔량', [{ value: 'Y', label: '잔량 있음' }]),
  ]],
  columns: ({ tc }) => [
    hiCol.edDateColumn({ header: '입고일자', field: 'inDate', size: 120, required: true }),
    hiCol.edComboColumn({ header: '제품', field: 'productUid', size: 200, required: true, options: productOptions(tc), align: 'left' }),
    hiCol.edNumberColumn({ header: '수량(매입단위)', field: 'buyQty', size: 110 }),
    hiCol.edNumberColumn({ header: '단가(매입단위)', field: 'buyUnitCost', size: 110 }),
    hiCol.roNumberColumn({ header: '입고수량(사용단위)', field: 'inQty', size: 130 }),
    hiCol.roTextColumn({ header: '사용단위', field: 'useUnit', size: 80, align: 'center' }),
    hiCol.roNumberColumn({ header: '사용단위단가', field: 'unitCost', size: 110 }),
    hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 110 }),
    hiCol.roNumberColumn({ header: '잔량', field: 'remainQty', size: 90 }),
    hiCol.edDateColumn({ header: '유통기한', field: 'expiryDate', size: 120 }),
    hiCol.edTextColumn({ header: '거래처', field: 'vendorNm', size: 140 }),
    hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
  ],
  load: (tc) => loadTable(tc, 'stockIn').map(decorate(tc)),
  filter,
  emptyRow: () => ({
    lotUid: createUid('LOT'),
    inDate: today(),
    productUid: '',
    buyQty: 1,
    buyUnitCost: 0,
    vendorNm: '',
    expiryDate: '',
    remark: '',
  }),
  validate: (rows, tc) => {
    const prev = new Map(loadTable(tc, 'stockIn').map((l) => [l.lotUid, l]));
    const errors = [];
    for (const r of rows) {
      const p = prev.get(r.lotUid);
      const issued = p ? num(p.inQty) - num(p.remainQty) : 0;
      if (r.mod === 'D') {
        if (issued > 0) errors.push(`출고된 입고건은 삭제할 수 없습니다: ${r.inDate} (출고 ${fmt(issued)})`);
        continue;
      }
      if (!r.inDate) errors.push('입고일자는 필수입니다.');
      if (!r.productUid) errors.push('제품을 선택하세요.');
      if (num(r.buyQty) <= 0) errors.push('수량은 0보다 커야 합니다.');
      if (p && issued > 0 && p.productUid !== r.productUid) errors.push(`출고된 입고건은 제품을 바꿀 수 없습니다: ${r.inDate}`);
    }
    return errors;
  },
  save: (tc, rows, v) => {
    const products = new Map(loadTable(tc, 'product').map((p) => [p.productUid, p]));
    const prev = new Map(loadTable(tc, 'stockIn').map((l) => [l.lotUid, l]));
    const short = [];
    persistTable(tc, 'stockIn', 'lotUid', rows, (r) => filter([r], v).length > 0, (r) => {
      const ratio = num(products.get(r.productUid)?.unitRatio) || 1;
      const inQty = Math.round(num(r.buyQty) * ratio * 10000) / 10000;
      const p = prev.get(r.lotUid);
      const issued = p ? num(p.inQty) - num(p.remainQty) : 0;
      const remainQty = Math.round((inQty - issued) * 10000) / 10000;
      if (remainQty < 0) short.push(r.inDate);
      const { useUnit, ...rest } = r;
      return {
        ...rest,
        inQty,
        unitCost: num(r.buyUnitCost) / ratio,
        amount: Math.round(num(r.buyQty) * num(r.buyUnitCost)),
        remainQty: Math.max(0, remainQty),
        createdAt: p?.createdAt || new Date().toISOString(),
      };
    });
    if (short.length) throw new Error(`이미 출고된 수량보다 적게 줄일 수 없습니다: ${short.join(', ')}`);
  },
};

const StockIn = () => <ShopGridPage cfg={cfg} />;
export default StockIn;
